import { useMemo, useState } from "react";
import { EM_ANDAMENTO, HISTORICO, STAGES, STAGE_ICON } from "../data/mockData";
import type {
  AndamentoProject,
  DateField,
  DrillDown,
  HistoricoProject,
  SortKey,
  TableRow,
} from "../types/timeEfficiency.types";
import { isoToBR, parseBR } from "../utils/format";

const ROWS_PER_PAGE = 20;
const SIDE_LIST_LIMIT = 15;
const DEFAULT_START = "2026-04-01";
const DEFAULT_END = "2026-09-03";

function todosStatusValores(): string[] {
  return Array.from(
    new Set([
      ...HISTORICO.map((p) => p.statusFinal || "Concluído"),
      ...EM_ANDAMENTO.map((p) => p.status),
    ])
  );
}

export const STATUS_OPTIONS = todosStatusValores();

function celulaOptions(): string[] {
  return [
    "Todas",
    ...Array.from(
      new Set([
        ...HISTORICO.map((p) => p.celula),
        ...EM_ANDAMENTO.map((p) => p.celula),
      ])
    ),
  ];
}

function coordenadorOptions(): string[] {
  return [
    "Todos",
    ...Array.from(
      new Set(
        [
          ...HISTORICO.map((p) => p.coordenador),
          ...EM_ANDAMENTO.map((p) => p.coordenador),
        ].filter((v): v is string => Boolean(v))
      )
    ),
  ];
}

function tipoEventoOptions(): string[] {
  return [
    "Todos",
    ...Array.from(
      new Set(
        [
          ...HISTORICO.map((p) => p.tipoEvento),
          ...EM_ANDAMENTO.map((p) => p.tipoEvento),
        ].filter((v): v is string => Boolean(v))
      )
    ),
  ];
}

function historicoParaLinhasTabela(hist: HistoricoProject[]): TableRow[] {
  return hist.map((p) => ({
    id: p.id,
    projeto: p.nomeEvento || p.cliente || p.id,
    cliente: p.cliente || "–",
    status: p.statusFinal || "Concluído",
    entrouEm: p.entrouEmFinal || "–",
    tempoNoStatus: p.tempoNoStatusFinal ?? 0,
    tempoTotal: p.total,
    inicio: isoToBR(p.inicio),
    criacao: isoToBR(p.criacao),
    justificativa: p.justificativaFinal || "–",
    reaberturas: p.reaberturas || 0,
    tipoEvento: p.tipoEvento || "",
    numPessoas: p.numPessoas ?? null,
  }));
}

function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

function average(values: number[]): number {
  return values.length ? sum(values) / values.length : 0;
}

export function useTimeEfficiencyDashboard() {
  const [filtroCelula, setFiltroCelula] = useState("Todas");
  const [filtroCoordenador, setFiltroCoordenador] = useState("Todos");
  const [filtroTipoEvento, setFiltroTipoEvento] = useState("Todos");
  const [filtroStatus, setFiltroStatus] = useState<Set<string>>(
    new Set(STATUS_OPTIONS)
  );
  const [filtroCampoData, setFiltroCampoData] = useState<DateField>("inicio");
  const [dataInicio, setDataInicio] = useState(DEFAULT_START);
  const [dataFim, setDataFim] = useState(DEFAULT_END);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("tempoNoStatus");
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [sideListExpanded, setSideListExpanded] = useState(false);
  const [drillDown, setDrillDown] = useState<DrillDown | null>(null);

  function clearFilters() {
    setFiltroCelula("Todas");
    setFiltroCoordenador("Todos");
    setFiltroTipoEvento("Todos");
    setFiltroStatus(new Set(STATUS_OPTIONS));
    setFiltroCampoData("inicio");
    setDataInicio(DEFAULT_START);
    setDataFim(DEFAULT_END);
    setCurrentPage(1);
    setDrillDown(null);
  }

  function toggleSort(key: SortKey) {
    setSortDir((prevDir) => (sortKey === key ? (-prevDir as 1 | -1) : -1));
    setSortKey(key);
    setCurrentPage(1);
  }

  function toggleDrillDown(next: DrillDown | null) {
    setDrillDown((prev) => {
      const mesmo =
        prev && next && prev.type === next.type && prev.etapa === next.etapa;
      return mesmo ? null : next;
    });
    setCurrentPage(1);
  }

  const dateRange = useMemo(() => {
    const start = new Date(dataInicio);
    const end = new Date(dataFim);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [dataInicio, dataFim]);

  const filteredHistorico = useMemo(() => {
    const { start, end } = dateRange;
    return HISTORICO.filter((p) => {
      if (filtroCelula !== "Todas" && p.celula !== filtroCelula) return false;
      if (filtroCoordenador !== "Todos" && p.coordenador !== filtroCoordenador)
        return false;
      if (filtroTipoEvento !== "Todos" && p.tipoEvento !== filtroTipoEvento)
        return false;
      const raw = filtroCampoData === "criacao" ? p.criacao : p.inicio;
      const d = new Date(raw);
      if (d < start || d > end) return false;
      return true;
    });
  }, [
    dateRange,
    filtroCelula,
    filtroCoordenador,
    filtroTipoEvento,
    filtroCampoData,
  ]);

  const filteredAndamento = useMemo(() => {
    const { start, end } = dateRange;
    return EM_ANDAMENTO.filter((p) => {
      if (filtroCelula !== "Todas" && p.celula !== filtroCelula) return false;
      if (filtroCoordenador !== "Todos" && p.coordenador !== filtroCoordenador)
        return false;
      if (filtroTipoEvento !== "Todos" && p.tipoEvento !== filtroTipoEvento)
        return false;
      if (!filtroStatus.has(p.status)) return false;
      const raw = filtroCampoData === "criacao" ? p.criacao : p.inicio;
      const d = parseBR(raw);
      if (d < start || d > end) return false;
      return true;
    });
  }, [
    dateRange,
    filtroCelula,
    filtroCoordenador,
    filtroTipoEvento,
    filtroStatus,
    filtroCampoData,
  ]);

  // Aviso de qualidade de dado: projetos com duração total abaixo de 1h
  // costumam ser testes ou dados incompletos.
  const dqWarning = useMemo(() => {
    const UMA_HORA_EM_DIAS = 1 / 24;
    const suspeitos =
      filteredHistorico.filter((p) => p.total < UMA_HORA_EM_DIAS).length +
      filteredAndamento.filter((p) => p.tempoTotal < UMA_HORA_EM_DIAS).length;
    const totalNoFiltro = filteredHistorico.length + filteredAndamento.length;
    if (suspeitos > 0 && totalNoFiltro > 0) {
      return { suspeitos, totalNoFiltro };
    }
    return null;
  }, [filteredHistorico, filteredAndamento]);

  // Tempo médio por etapa (não-drilled): usado pros KPIs "presoAgora" (etapa
  // com mais parados) e "reaberturas", que olham a seleção de filtros normal,
  // sem o drill-down de um KPI aplicado.
  const stageStatsBase = useMemo(() => {
    const stageAvg: Record<string, number> = {};
    const stageCountHist: Record<string, number> = {};
    const andamentoAvg: Record<string, number> = {};
    const andamentoCount: Record<string, number> = {};
    STAGES.forEach((s) => {
      const vals = filteredHistorico
        .filter((p) => p.etapas.some((e) => e.nome === s))
        .map((p) => p.etapas.find((e) => e.nome === s)!.dias);
      stageAvg[s] = average(vals);
      stageCountHist[s] = vals.length;

      const valsA = filteredAndamento
        .filter((p) => p.status === s)
        .map((p) => p.tempoNoStatus);
      andamentoAvg[s] = average(valsA);
      andamentoCount[s] = valsA.length;
    });
    return { stageAvg, stageCountHist, andamentoAvg, andamentoCount };
  }, [filteredHistorico, filteredAndamento]);

  const kpis = useMemo(() => {
    const { andamentoAvg, andamentoCount, stageAvg } = stageStatsBase;

    const presoAgoraEntry = Object.entries(andamentoCount)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])[0];
    const presoAgora = presoAgoraEntry
      ? {
          etapa: presoAgoraEntry[0],
          count: presoAgoraEntry[1],
          media: andamentoAvg[presoAgoraEntry[0]],
        }
      : null;

    const todosNoFiltroReab = [...filteredHistorico, ...filteredAndamento];
    const comReabertura = todosNoFiltroReab.filter(
      (p: any) => (p.reaberturas || 0) > 0
    ).length;
    const maxReaberturas = todosNoFiltroReab.reduce(
      (a, p: any) => Math.max(a, p.reaberturas || 0),
      0
    );
    const pctComReabertura = todosNoFiltroReab.length
      ? Math.round((comReabertura / todosNoFiltroReab.length) * 100)
      : 0;

    if (filteredHistorico.length === 0) {
      return {
        hasConcluded: false,
        concludedCount: 0,
        tempoMedioTotal: 0,
        mediana: 0,
        slowest: null as { etapa: string; media: number } | null,
        acimaMedia: 0,
        pctAcimaMedia: 0,
        presoAgora,
        comReabertura,
        maxReaberturas,
        pctComReabertura,
        totalNoFiltroReab: todosNoFiltroReab.length,
      };
    }

    const tempoMedioTotal = average(filteredHistorico.map((p) => p.total));
    const sortedTotals = filteredHistorico
      .map((p) => p.total)
      .sort((a, b) => a - b);
    const midIdx = Math.floor(sortedTotals.length / 2);
    const mediana =
      sortedTotals.length % 2 === 0
        ? (sortedTotals[midIdx - 1] + sortedTotals[midIdx]) / 2
        : sortedTotals[midIdx];
    const slowestEntry =
      Object.entries(stageAvg)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])[0] ?? Object.entries(stageAvg)[0];
    const slowest = slowestEntry
      ? { etapa: slowestEntry[0], media: slowestEntry[1] }
      : null;
    const acimaMedia = filteredHistorico.filter(
      (p) => p.total > tempoMedioTotal
    ).length;
    const pctAcimaMedia = Math.round(
      (acimaMedia / filteredHistorico.length) * 100
    );

    return {
      hasConcluded: true,
      concludedCount: filteredHistorico.length,
      tempoMedioTotal,
      mediana,
      slowest,
      acimaMedia,
      pctAcimaMedia,
      presoAgora,
      comReabertura,
      maxReaberturas,
      pctComReabertura,
      totalNoFiltroReab: todosNoFiltroReab.length,
    };
  }, [filteredHistorico, filteredAndamento, stageStatsBase]);

  // Drill-down: filtra TODOS os painéis abaixo dos KPIs (gráfico de etapas,
  // rankings, lista de parados) -- os próprios cards de KPI nunca são
  // afetados pelo próprio drill-down que eles geram.
  const histParaPaineis = useMemo(() => {
    if (!drillDown) return filteredHistorico;
    if (drillDown.type === "etapaLenta") {
      return filteredHistorico.filter((p) =>
        p.etapas.some((e) => e.nome === drillDown.etapa)
      );
    }
    if (drillDown.type === "acimaMedia") {
      return filteredHistorico.filter((p) => p.total > kpis.tempoMedioTotal);
    }
    if (drillDown.type === "reabertura") {
      return filteredHistorico.filter((p) => (p.reaberturas || 0) > 0);
    }
    return []; // etapaParada é só sobre em andamento
  }, [drillDown, filteredHistorico, kpis.tempoMedioTotal]);

  const andaParaPaineis = useMemo(() => {
    if (!drillDown) return filteredAndamento;
    if (drillDown.type === "etapaParada") {
      return filteredAndamento.filter((p) => p.status === drillDown.etapa);
    }
    if (drillDown.type === "reabertura") {
      return filteredAndamento.filter((p) => (p.reaberturas || 0) > 0);
    }
    return []; // etapaLenta/acimaMedia são só sobre concluídos
  }, [drillDown, filteredAndamento]);

  const stageChartData = useMemo(() => {
    const maxShared = Math.max(
      1,
      ...STAGES.map((s) => {
        const h = histParaPaineis
          .filter((p) => p.etapas.some((e) => e.nome === s))
          .map((p) => p.etapas.find((e) => e.nome === s)!.dias);
        const a = andaParaPaineis
          .filter((p) => p.status === s)
          .map((p) => p.tempoNoStatus);
        return Math.max(average(h), average(a));
      })
    );
    return STAGES.map((s, i) => {
      const histVals = histParaPaineis
        .filter((p) => p.etapas.some((e) => e.nome === s))
        .map((p) => p.etapas.find((e) => e.nome === s)!.dias);
      const andaVals = andaParaPaineis
        .filter((p) => p.status === s)
        .map((p) => p.tempoNoStatus);
      return {
        nome: s,
        icon: STAGE_ICON_OF(s),
        colorIndex: i,
        avgConcluidos: histVals.length ? average(histVals) : null,
        countConcluidos: histVals.length,
        avgAndamento: andaVals.length ? average(andaVals) : null,
        countAndamento: andaVals.length,
        maxShared,
      };
    });
  }, [histParaPaineis, andaParaPaineis]);

  const celulaRanking = useMemo(() => {
    const stats: Record<string, number[]> = {};
    histParaPaineis.forEach((p) => {
      (stats[p.celula] ??= []).push(p.total);
    });
    andaParaPaineis.forEach((p) => {
      (stats[p.celula] ??= []).push(p.tempoTotal);
    });
    return Object.entries(stats)
      .map(([nome, vals]) => ({
        nome,
        media: average(vals),
        count: vals.length,
      }))
      .sort((a, b) => b.media - a.media);
  }, [histParaPaineis, andaParaPaineis]);

  const coordenadorRanking = useMemo(() => {
    const stats: Record<string, { maxDias: number; count: number }> = {};
    andaParaPaineis.forEach((p) => {
      if (!p.coordenador) return;
      const atual = stats[p.coordenador] ?? { maxDias: 0, count: 0 };
      atual.count += 1;
      if (p.tempoNoStatus > atual.maxDias) atual.maxDias = p.tempoNoStatus;
      stats[p.coordenador] = atual;
    });
    return Object.entries(stats)
      .map(([nome, v]) => ({ nome, media: v.maxDias, count: v.count }))
      .sort((a, b) => b.media - a.media)
      .slice(0, 10);
  }, [andaParaPaineis]);

  const coordenadorHasAnyData = useMemo(
    () => [...HISTORICO, ...EM_ANDAMENTO].some((p: any) => p.coordenador),
    []
  );

  const tipoEventoRanking = useMemo(() => {
    const stats: Record<string, number[]> = {};
    histParaPaineis.forEach((p) => {
      if (!p.tipoEvento) return;
      (stats[p.tipoEvento] ??= []).push(p.total);
    });
    andaParaPaineis.forEach((p) => {
      if (!p.tipoEvento) return;
      (stats[p.tipoEvento] ??= []).push(p.tempoTotal);
    });
    return Object.entries(stats)
      .map(([nome, vals]) => ({
        nome,
        media: average(vals),
        count: vals.length,
      }))
      .sort((a, b) => b.media - a.media);
  }, [histParaPaineis, andaParaPaineis]);

  const tipoEventoHasAnyData = useMemo(
    () => [...HISTORICO, ...EM_ANDAMENTO].some((p: any) => p.tipoEvento),
    []
  );

  const staleProjects = useMemo(
    () =>
      [...andaParaPaineis].sort((a, b) => b.tempoNoStatus - a.tempoNoStatus),
    [andaParaPaineis]
  );
  const staleProjectsVisible = sideListExpanded
    ? staleProjects
    : staleProjects.slice(0, SIDE_LIST_LIMIT);

  // Monta o conjunto de linhas da tabela/exportação, aplicando o filtro de
  // status normal e, quando houver, o drill-down de um dos KPIs clicáveis.
  const tableBaseRows: (TableRow | AndamentoProject)[] = useMemo(() => {
    if (drillDown) {
      if (drillDown.type === "etapaLenta") {
        const filtrado = filteredHistorico.filter((p) =>
          p.etapas.some((e) => e.nome === drillDown.etapa)
        );
        return historicoParaLinhasTabela(filtrado).filter((p) =>
          filtroStatus.has(p.status)
        );
      }
      if (drillDown.type === "acimaMedia") {
        const filtrado = filteredHistorico.filter(
          (p) => p.total > kpis.tempoMedioTotal
        );
        return historicoParaLinhasTabela(filtrado).filter((p) =>
          filtroStatus.has(p.status)
        );
      }
      if (drillDown.type === "etapaParada") {
        return filteredAndamento.filter((p) => p.status === drillDown.etapa);
      }
      if (drillDown.type === "reabertura") {
        const linhasHistorico = historicoParaLinhasTabela(
          filteredHistorico
        ).filter((p) => filtroStatus.has(p.status));
        return [...linhasHistorico, ...filteredAndamento].filter(
          (p: any) => (p.reaberturas || 0) > 0
        );
      }
    }
    const linhasHistorico = historicoParaLinhasTabela(filteredHistorico).filter(
      (p) => filtroStatus.has(p.status)
    );
    return [...linhasHistorico, ...filteredAndamento];
  }, [
    drillDown,
    filteredHistorico,
    filteredAndamento,
    filtroStatus,
    kpis.tempoMedioTotal,
  ]);

  const tableRowsAll = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let rows = tableBaseRows.filter(
      (p) =>
        p.id.toLowerCase().includes(term) ||
        p.projeto.toLowerCase().includes(term) ||
        p.cliente.toLowerCase().includes(term)
    ) as TableRow[];
    rows = [...rows].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "number" && typeof vb === "number")
        return sortDir * (va - vb);
      return sortDir * String(va).localeCompare(String(vb));
    });
    return rows;
  }, [tableBaseRows, searchTerm, sortKey, sortDir]);

  const totalPages = Math.max(
    1,
    Math.ceil(tableRowsAll.length / ROWS_PER_PAGE)
  );
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const startIdx = (safePage - 1) * ROWS_PER_PAGE;
  const tableRowsPage = tableRowsAll.slice(startIdx, startIdx + ROWS_PER_PAGE);

  function kpiClickEtapaParada() {
    if (!kpis.presoAgora) return;
    setSortKey("tempoNoStatus");
    setSortDir(-1);
    toggleDrillDown({ type: "etapaParada", etapa: kpis.presoAgora.etapa });
  }
  function kpiClickEtapaLenta() {
    if (!kpis.slowest) return;
    setSortKey("tempoTotal");
    setSortDir(-1);
    toggleDrillDown({ type: "etapaLenta", etapa: kpis.slowest.etapa });
  }
  function kpiClickAcimaMedia() {
    setSortKey("tempoTotal");
    setSortDir(-1);
    toggleDrillDown({ type: "acimaMedia" });
  }
  function kpiClickReabertura() {
    setSortKey("reaberturas");
    setSortDir(-1);
    toggleDrillDown({ type: "reabertura" });
  }

  return {
    // filtros
    filtroCelula,
    setFiltroCelula: (v: string) => {
      setFiltroCelula(v);
      setCurrentPage(1);
    },
    filtroCoordenador,
    setFiltroCoordenador: (v: string) => {
      setFiltroCoordenador(v);
      setCurrentPage(1);
    },
    filtroTipoEvento,
    setFiltroTipoEvento: (v: string) => {
      setFiltroTipoEvento(v);
      setCurrentPage(1);
    },
    filtroStatus,
    setFiltroStatus: (v: Set<string>) => {
      setFiltroStatus(v);
      setCurrentPage(1);
    },
    filtroCampoData,
    setFiltroCampoData: (v: DateField) => {
      setFiltroCampoData(v);
      setCurrentPage(1);
    },
    dataInicio,
    setDataInicio: (v: string) => {
      setDataInicio(v);
      setCurrentPage(1);
    },
    dataFim,
    setDataFim: (v: string) => {
      setDataFim(v);
      setCurrentPage(1);
    },
    searchTerm,
    setSearchTerm: (v: string) => {
      setSearchTerm(v);
      setCurrentPage(1);
    },
    clearFilters,

    celulaOptions: celulaOptions(),
    coordenadorOptions: coordenadorOptions(),
    tipoEventoOptions: tipoEventoOptions(),
    statusOptions: STATUS_OPTIONS,

    // dados derivados
    dqWarning,
    kpis,
    stageChartData,
    celulaRanking,
    coordenadorRanking,
    coordenadorHasAnyData,
    tipoEventoRanking,
    tipoEventoHasAnyData,
    staleProjects: staleProjectsVisible,
    staleProjectsTotal: staleProjects.length,
    sideListExpanded,
    setSideListExpanded,

    drillDown,
    clearDrillDown: () => toggleDrillDown(null),
    // Exposto pra permitir montar um drill-down com dado vindo de fora
    // (ex.: "etapa parada" real, calculado no `useTimeEfficiencyRealData").
    toggleDrillDown,
    kpiClickEtapaParada,
    kpiClickEtapaLenta,
    kpiClickAcimaMedia,
    kpiClickReabertura,

    // tabela
    tableRows: tableRowsPage,
    tableRowsAllCount: tableRowsAll.length,
    sortKey,
    sortDir,
    toggleSort,
    currentPage: safePage,
    totalPages,
    setCurrentPage,

    // pra export CSV
    getExportRows: () => tableRowsAll,
  };
}

function STAGE_ICON_OF(stage: string): string {
  return STAGE_ICON[stage] ?? stage.slice(0, 2).toUpperCase();
}
